import React, { useState } from 'react';
import { Row, Col, Card, Button, Form, Input, Typography, message, Modal, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getUserData, saveUserData } from '../constants';
import axiosInstance from '../../axiosInstance';
import { LockOutlined, MailOutlined, PhoneOutlined, SafetyCertificateOutlined, ShoppingCartOutlined, StockOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = (values) => {
        setLoading(true);

        axiosInstance.post('login', values)
            .then(response => {
                if (response.status === 200) {
                    const { user, token } = response.data;

                    localStorage.setItem('token', token);
                    axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
                    const shops = user.employees?.map(employee => employee.shop) || [];
                    saveUserData({
                        id: user.id,
                        retailer_id: user.retailer_id,
                        retailer_number: user.retailer?.retailer_number,
                        name: user.name,
                        email: user.email,
                        phone: user.phone,
                        profile_pic: user.profile_pic,
                        role: user.role,
                        shops: shops,  
                        user_permissions: user.user_permission
                    });
                    localStorage.setItem('retailerId', user.retailer_id);
                    const userData = getUserData() || {};
                    const userShops = userData?.shops || [];
                    const selectedShop = userShops.length > 0 ? userShops[0] : null;
                    localStorage.setItem('employeeId', user.id);

                    if (selectedShop) {
                        localStorage.setItem('shopId', selectedShop.id);
                        message.success(`Default shop set to: ${selectedShop.name}`);
                    }

                    return axiosInstance.get('check-package-limit');
                }
                throw new Error('Login failed');
            })
            .then((middlewareResponse) => {
                if (middlewareResponse.status === 200) {
                    navigate('/');
                    window.location.reload();
                }
            })
            .catch(error => {
                if (error.response) {
                    if (error.response.status === 401) {
                        message.error('Invalid credentials');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                    } else if (error.response.status === 422) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error('Validation error');
                    } else if (error.response.status === 403) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error(error.response.data.message);
                        navigate('/in-touch/upgrade');
                    } else if (error.response.status === 404) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error(error.response.data.message);
                        navigate('/in-touch/register');
                    } else {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error('An error occurred. Please try again later.');
                    }
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    message.error('Network error. Please try again later.');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleForgotPassword = (values) => {
        setForgotPasswordLoading(true);

        axiosInstance.post('reset-password', values)
            .then(response => {
                if (response.status === 200) {
                    message.success(response.data.message);
                    setIsForgotPasswordModalVisible(false);
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 404) {
                    message.error('Phone number not found.');
                } else if (error.response && error.response.status === 422) {
                    const errors = error.response.data.errors;
                    if (errors.phone) {
                        message.error(errors.phone[0]);
                    } else {
                        message.error('Validation error.');
                    }
                } else {
                    message.error('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setForgotPasswordLoading(false);
            });
    };

    return (
        <div className="login-container" style={{
            minHeight: '100vh',
            display: 'flex',
            backgroundColor: '#f8fafc',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            overflowX: 'hidden',
            overflowY: 'auto'
        }}>
            {styleTag}
            
            <Row style={{ width: '100%', margin: 0 }}>
                {/* Left Panel: Branding & Slogan (using dashboard sidebar background #001529) */}
                <Col xs={0} md={12} className="login-left-panel" style={{
                    background: 'linear-gradient(135deg, #001529 0%, #0c2540 60%, #17385c 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                }}>
                    {/* Glowing background accent overlay */}
                    <div style={{
                        position: 'absolute',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(24, 144, 255, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
                        top: '10%',
                        left: '10%',
                        filter: 'blur(30px)'
                    }} />

                    <div style={{ maxWidth: '440px', position: 'relative', zIndex: 2 }}>
                        {/* Logo Wrapper */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px' }}>
                            <div style={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                                padding: '8px', 
                                borderRadius: '12px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                            }}>
                                <img
                                    src="/images/sell-smart-logo.png"
                                    alt="Sell Smart Logo"
                                    style={{ height: '40px', objectFit: 'contain' }}
                                />
                            </div>
                            <span style={{ fontSize: '24px', fontWeight: '800', color: '#ffffff', letterSpacing: '-0.5px' }}>
                                SellSmart
                            </span>
                        </div>

                        {/* Tagline */}
                        <Title className="login-tagline-title" level={1} style={{ color: '#ffffff', fontWeight: '800', lineHeight: '1.2', letterSpacing: '-1px', marginBottom: '20px' }}>
                            Supercharge Your Retail Enterprise.
                        </Title>
                        <Text className="login-tagline-text" style={{ color: '#e0e7ff', display: 'block', marginBottom: '40px' }}>
                            Smart inventory tracking, real-time POS processing, and automatic permission management tailored for modern business environments.
                        </Text>

                        {/* Interactive Feature Cards */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div className="login-feature-card">
                                <div className="login-icon-box bg-white-glow">
                                    <StockOutlined style={{ color: '#ffffff' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', color: '#ffffff', fontSize: '14px' }}>Real-time Inventory Management</div>
                                    <div style={{ color: '#e0e7ff', fontSize: '13px', marginTop: '2px' }}>Track stock values, category listings, and expiration levels effortlessly.</div>
                                </div>
                            </div>

                            <div className="login-feature-card">
                                <div className="login-icon-box bg-white-glow">
                                    <ShoppingCartOutlined style={{ color: '#ffffff' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', color: '#ffffff', fontSize: '14px' }}>Intuitive POS Processing</div>
                                    <div style={{ color: '#e0e7ff', fontSize: '13px', marginTop: '2px' }}>Check out orders, print invoice slips, and track multi-channel deliveries.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Right Panel: Login Credentials Form (using active menu item color #1890ff) */}
                <Col xs={24} md={12} className="login-right-panel" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8fafc'
                }}>
                    <Card
                        bordered={false}
                        className="login-card"
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            background: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.06)',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <div style={{ marginBottom: '32px' }}>
                            <div className="mobile-login-logo" style={{ display: 'none', justifyContent: 'center', marginBottom: '24px' }}>
                                <img
                                    src="/images/sell-smart-logo.png"
                                    alt="Sell Smart Logo"
                                    style={{ height: '44px', objectFit: 'contain' }}
                                />
                            </div>
                            <Title level={2} style={{ color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '8px' }}>
                                Sign In
                            </Title>
                            <Text style={{ color: '#64748b', fontSize: '14px' }}>
                                Enter your credentials to access your administrative dashboard.
                            </Text>
                        </div>

                        <Form
                            layout="vertical"
                            onFinish={handleSubmit}
                            size="large"
                        >
                            <Form.Item
                                label={<span style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Email Address</span>}
                                name="email"
                                rules={[
                                    { required: true, message: 'Email address is required' },
                                    { type: 'email', message: 'Enter a valid email structure' },
                                ]}
                                style={{ marginBottom: '20px' }}
                            >
                                <Input
                                    prefix={<MailOutlined style={{ color: '#94a3b8', marginRight: '6px' }} />}
                                    placeholder="Enter your email"
                                    className="login-custom-input"
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <span style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Password</span>
                                        <span 
                                            style={{ color: '#1890ff', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                                            onClick={() => setIsForgotPasswordModalVisible(true)}
                                        >
                                            Forgot?
                                        </span>
                                    </div>
                                }
                                name="password"
                                rules={[{ required: true, message: 'Password is required' }]}
                                style={{ marginBottom: '24px', width: '100%' }}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{ color: '#94a3b8', marginRight: '6px' }} />}
                                    placeholder="••••••••"
                                    className="login-custom-input"
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: '24px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                    style={{
                                        height: '46px',
                                        borderRadius: '8px',
                                        fontWeight: '700',
                                        fontSize: '15px',
                                        background: 'linear-gradient(135deg, #1890ff 0%, #001529 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 14px rgba(24, 144, 255, 0.2)'
                                    }}
                                >
                                    Log In
                                </Button>
                            </Form.Item>

                            <Divider style={{ borderColor: '#e2e8f0', color: '#94a3b8', fontSize: '12px' }}>or</Divider>

                            {/* <div style={{ textAlign: 'center', marginTop: '24px' }}>
                                <Text style={{ color: '#64748b', fontSize: '14px' }}>
                                    Need a new setup?{' '}
                                    <Text
                                        strong
                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                        onClick={() => navigate('/in-touch/register')}
                                    >
                                        Register Branch
                                    </Text>
                                </Text>
                            </div> */}
                        </Form>
                    </Card>
                </Col>
            </Row>

            {/* Forgot Password Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', fontSize: '16px', fontWeight: '700' }}>
                        <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
                        <span>Recover Credentials</span>
                    </div>
                }
                visible={isForgotPasswordModalVisible}
                onCancel={() => setIsForgotPasswordModalVisible(false)}
                footer={null}
                centered
                destroyOnClose
                bodyStyle={{ padding: '24px', backgroundColor: '#ffffff' }}
                style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
                <div style={{ marginBottom: '20px' }}>
                    <Text style={{ color: '#64748b', fontSize: '14px' }}>
                        Enter your registered phone number below. We will transmit instructions to recover access.
                    </Text>
                </div>
                <Form layout="vertical" onFinish={handleForgotPassword}>
                    <Form.Item
                        label={<span style={{ color: '#475569', fontWeight: '600' }}>Phone Number</span>}
                        name="phone"
                        rules={[{ required: true, message: 'Phone number is required' }]}
                        style={{ marginBottom: '24px' }}
                    >
                        <Input
                            prefix={<PhoneOutlined style={{ color: '#94a3b8', marginRight: '6px' }} />}
                            placeholder="e.g. 0712345678"
                            className="login-custom-input"
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={forgotPasswordLoading}
                        block
                        style={{
                            height: '46px',
                            borderRadius: '8px',
                            fontWeight: '700',
                            fontSize: '15px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #001529 100%)',
                            border: 'none'
                        }}
                    >
                        Send Reset Link
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

const styleTag = (
    <style>
        {`
        .login-left-panel {
            padding: 60px;
        }
        .login-right-panel {
            padding: 24px;
        }
        .login-card .ant-card-body {
            padding: 32px !important;
        }
        .login-tagline-title {
            font-size: 38px !important;
        }
        .login-tagline-text {
            font-size: 16px;
        }
        
        @media (max-width: 992px) {
            .login-left-panel {
                padding: 40px 20px;
            }
            .login-tagline-title {
                font-size: 32px !important;
            }
            .login-tagline-text {
                font-size: 15px;
            }
        }
        
        @media (max-width: 768px) {
            .login-container {
                min-height: 100vh;
                height: 100%;
            }
            .login-left-panel {
                display: none !important;
            }
            .mobile-login-logo {
                display: flex !important;
            }
            .login-right-panel {
                padding: 16px;
            }
            .login-card .ant-card-body {
                padding: 24px !important;
            }
        }
        
        @media (max-width: 480px) {
            .login-right-panel {
                padding: 12px;
            }
            .login-card .ant-card-body {
                padding: 20px !important;
            }
            .login-card {
                border-radius: 12px !important;
            }
        }

        .login-feature-card {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            padding: 16px;
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.12);
            transition: all 0.3s ease;
        }
        .login-feature-card:hover {
            background: rgba(255, 255, 255, 0.12);
            border-color: rgba(255, 255, 255, 0.2);
            transform: translateX(4px);
        }
        .login-icon-box {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;
        }
        .login-icon-box.bg-white-glow {
            background: rgba(255, 255, 255, 0.12);
        }
        .login-custom-input {
            background: #ffffff !important;
            border: 1px solid #d9d9d9 !important;
            color: #000000 !important;
            height: 46px !important;
            border-radius: 8px !important;
            padding: 8px 12px !important;
            transition: all 0.3s ease !important;
        }
        .login-custom-input:focus, .login-custom-input-focused {
            border-color: #1890ff !important;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
        }
        .login-custom-input input {
            background: transparent !important;
            color: #000000 !important;
        }
        .ant-modal-content {
            background-color: #ffffff !important;
            border: 1px solid #e2e8f0 !important;
        }
        .ant-modal-header {
            background-color: #ffffff !important;
            border-bottom: 1px solid #e2e8f0 !important;
        }
        .ant-modal-close-icon {
            color: #64748b !important;
        }
        `}
    </style>
);

export default Login;